import { cx } from "~/lib/legacy-utils"

export type GaugeKind = "danger" | "success" | "warning"

const colorByKind: Record<GaugeKind, string> = {
  danger: "text-red-600 dark:text-red-400",
  success: "text-green-600 dark:text-green-400",
  warning: "text-orange-600 dark:text-orange-400",
}

export function Gauge({
  caption,
  kind,
  percent,
}: Readonly<{
  caption: string
  kind: GaugeKind
  percent: number
}>) {
  const strokeOffset = 100 - percent

  return (
    <div className="relative mx-auto mt-5 w-48 max-w-full">
      <svg className="block h-auto w-full overflow-visible" viewBox="0 0 120 70" aria-hidden>
        <path
          className="fill-none stroke-border stroke-8"
          d="M6 64 A54 54 0 0 1 114 64"
          strokeLinecap="round"
        />
        <path
          className={cx(
            "fill-none stroke-current stroke-8 transition-all duration-700",
            colorByKind[kind],
          )}
          d="M6 64 A54 54 0 0 1 114 64"
          pathLength={100}
          strokeDasharray={100}
          strokeDashoffset={strokeOffset}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-x-0 bottom-0 flex flex-col items-center">
        <span className={cx("text-4xl font-semibold leading-none", colorByKind[kind])}>
          {percent}%
        </span>
        <span className="mt-2 font-mono text-xs font-medium uppercase tracking-widest text-muted-foreground">
          {caption}
        </span>
      </div>
    </div>
  )
}

export function MiniGauge({
  allowed,
  kind,
  label,
  percent,
  total,
}: Readonly<{
  allowed: number
  kind: GaugeKind
  label: string
  percent: number
  total: number
}>) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative size-14">
        <svg className="-rotate-90 size-full" viewBox="0 0 56 56" aria-hidden>
          <circle className="fill-none stroke-border stroke-4" cx="28" cy="28" r="24" />
          <circle
            className={cx(
              "fill-none stroke-current stroke-4 transition-all duration-700",
              colorByKind[kind],
            )}
            cx="28"
            cy="28"
            r="24"
            pathLength={100}
            strokeDasharray={100}
            strokeDashoffset={100 - percent}
            strokeLinecap="round"
          />
        </svg>
        <span
          className={cx(
            "absolute inset-0 flex items-center justify-center text-xs font-semibold",
            colorByKind[kind],
          )}
        >
          {percent}%
        </span>
      </div>
      <span className="mt-2 whitespace-nowrap text-sm font-medium">{label}</span>
      <span className="font-mono text-xs text-muted-foreground">
        {allowed}/{total}
      </span>
    </div>
  )
}
